import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppModuleLayout } from "@layouts";
import {
    BreadcrumbsCommon,
    ClipLoaderCommon,
    StyledSavedCartPage,
    StyledButton,
    PendingRequestPage,
} from "@components";
import { PATH_HOME, PATH_QUOTES } from "@routes";
import { useAppDispatch, useAppSelector } from "@redux";
import { loadSharedQuotes, loadAllQuotes, loadQuotes } from "src/Redux/Slices/allQuotes";
import { loadMyQuotes } from "src/Redux/Slices/myQuotes";
import { theme } from "@utilities";
import styled from "styled-components";
import { LoadingStage } from "src/Interfaces/shared";

export const PendingRequests = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const sharedToMeQuotes = useAppSelector((state) => state.allQuotes.sharedQuotes);
    const quotesLoadingStage = useAppSelector((state) => state.allQuotes.loadingStage);
    const isLoading = quotesLoadingStage === LoadingStage.IS_LOADING;
    const isOrderTabEnabled = Boolean(
        useAppSelector((state) => state.account.metadata?.is_order_tab_enabled),
    );

    useEffect(() => {
        dispatch(loadSharedQuotes());
    }, [dispatch]);

    const handleQuoteAction = () => {
        dispatch(loadSharedQuotes());
        if (isOrderTabEnabled) {
            dispatch(loadAllQuotes());
            dispatch(loadMyQuotes());
        } else {
            dispatch(loadQuotes());
        }
    };

    const breadcrumbs = [
        { title: "Home", link: PATH_HOME },
        { title: isOrderTabEnabled ? "Orders" : "Quotes", link: PATH_QUOTES },
        { title: "Pending Requests", link: "" },
    ];

    if (isLoading) {
        return (
            <AppModuleLayout>
                <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
            </AppModuleLayout>
        );
    }

    return (
        <AppModuleLayout>
            <StyledSavedCartPage>
                <div className="wrapper">
                    <BreadcrumbsWrapper>
                        <div className="breadCrumbs">
                            <BreadcrumbsCommon data={breadcrumbs} />
                        </div>
                    </BreadcrumbsWrapper>

                    <div className="subWrapper">
                        <div className="listCarts">
                            <Header>
                                <Title>
                                    Pending Requests{" "}
                                    <Count>({sharedToMeQuotes?.length || 0})</Count>
                                </Title>
                            </Header>
                            {sharedToMeQuotes.length === 0 ? (
                                <EmptyState>
                                    <EmptyTitle>You don't have any pending requests.</EmptyTitle>
                                    <EmptySubtitle>
                                        You'll find all your pending requests here.
                                    </EmptySubtitle>
                                    <EmptyButtonWrapper>
                                        <BackButton
                                            color={theme.gradients.button}
                                            onClick={() => navigate(PATH_QUOTES)}
                                        >
                                            Go Back to Quotes
                                        </BackButton>
                                    </EmptyButtonWrapper>
                                </EmptyState>
                            ) : (
                                <PendingRequestPage
                                    data={sharedToMeQuotes}
                                    onQuoteAction={handleQuoteAction}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </StyledSavedCartPage>
        </AppModuleLayout>
    );
};

const BreadcrumbsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;

    .breadCrumbs {
        flex: 1;
    }
`;

const Header = styled.div`
    margin-bottom: 20px;
`;

const Title = styled.p`
    font-size: 20px;
    font-weight: 700;
    margin: 0;
`;

const Count = styled.span`
    color: ${theme.colors.items_color};
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 350px;
    border-radius: ${theme.borderRadius.large};
    margin: 40px 0;
`;

const EmptyTitle = styled.div`
    font-weight: 700;
    font-size: 1rem;
    text-align: center;
    margin-bottom: 12px;
    color: #222;
`;

const EmptySubtitle = styled.div`
    color: #9e9e9e;
    font-size: 1rem;
    margin-bottom: 32px;
    text-align: center;
`;

const EmptyButtonWrapper = styled.div`
    width: 320px;
    max-width: 100%;
`;

const BackButton = styled(StyledButton)`
    width: 100%;
    height: 50px;
    font-size: 16px;
    font-weight: 600;
`;
